/**
 * inventory response PROJECTIONS — hand-composed from generated atoms.
 *
 * Covers the products / stock / stock-order / stock-take response surface. The
 * inventory-adjacent brand / category / supplier lookups live in ./catalog.ts
 * (authored by the catalog agent) — this file owns the rest.
 *
 * Follows the proof pattern in ./leads.ts. Each response exports a schema plus
 * its `z.infer` type. Names are chosen not to collide with sibling response
 * files (esp. catalog's `productBrand*` / `productCategory*` / `supplier*`) or
 * the re-exported `*AtomSchema` names.
 */
import { z } from 'zod';
import { productAtomSchema, productStockAtomSchema, stockOrderAtomSchema, stockOrderFeeAtomSchema, stockOrderItemAtomSchema, stockTakeAtomSchema, stockTakeItemAtomSchema, } from '../generated/index.js';
// ============================================================================
// PRODUCTS
// ============================================================================
/**
 * A product on the wire.
 *
 * The atom types `images` / `skus` as `z.unknown()` (the generator can't see
 * through `jsonb().$type<string[]>()`), so they are hand-narrowed here to
 * `string[] | null` to match the DB column shape. Money fields
 * (`supplyPriceCents`, `retailPriceCents`) are integer cents.
 */
export const productSchema = productAtomSchema.extend({
    images: z.array(z.string()).nullable(),
    skus: z.array(z.string()).nullable(),
});
/**
 * A listed product plus the branches that stock it.
 *
 * LIST only, mirroring `listedServiceSchema`, and EMPTY MEANS EVERY BRANCH —
 * not "stocked nowhere". Per-branch QUANTITY is `product_stock`, a different
 * thing entirely: this answers "is it sold here at all?".
 */
export const listedProductSchema = productSchema.extend({
    locationIds: z.array(z.string()),
});
/** `GET /products` — the `{ items, total, limit, offset }` list wrapper. */
export const listProductsResponseSchema = z.object({
    items: z.array(listedProductSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
});
// ============================================================================
// STOCK (per-location product stock)
// ============================================================================
/** A per-location product-stock row (the atom, verbatim). */
export const productStockSchema = productStockAtomSchema;
/**
 * `GET /products/:id/stock` — a bare array of per-location stock rows for one
 * product (no wrapper).
 */
export const listProductStockResponseSchema = z.array(productStockSchema);
// ============================================================================
// STOCK ORDERS
// ============================================================================
/** A stock order on the wire (the atom, verbatim). */
export const stockOrderSchema = stockOrderAtomSchema;
/** A stock-order line item (the atom, verbatim — `unitCostCents` is cents). */
export const stockOrderItemSchema = stockOrderItemAtomSchema;
/**
 * A stock-order fee (the atom, verbatim). `value` is a raw number whose meaning
 * depends on `type` (integer cents for a flat fee, whole-number percent
 * otherwise) — mirrors the flat DB row.
 */
export const stockOrderFeeSchema = stockOrderFeeAtomSchema;
/**
 * `GET /stock-orders/:id` — the detail projection: the stock order joined with
 * its expanded line items and fees.
 */
export const stockOrderWithItemsSchema = stockOrderSchema.extend({
    items: z.array(stockOrderItemSchema),
    fees: z.array(stockOrderFeeSchema),
});
/** `GET /stock-orders` — the `{ items, total, limit, offset }` list wrapper. */
export const listStockOrdersResponseSchema = z.object({
    items: z.array(stockOrderSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
});
// ============================================================================
// STOCK TAKES
// ============================================================================
/** A stock take on the wire (the atom, verbatim). */
export const stockTakeSchema = stockTakeAtomSchema;
/** A stock-take line item (the atom, verbatim). */
export const stockTakeItemSchema = stockTakeItemAtomSchema;
/**
 * `GET /stock-takes/:id` — the detail projection: the stock take joined with
 * its counted line items.
 */
export const stockTakeWithItemsSchema = stockTakeSchema.extend({
    items: z.array(stockTakeItemSchema),
});
/** `GET /stock-takes` — the `{ items, total, limit, offset }` list wrapper. */
export const listStockTakesResponseSchema = z.object({
    items: z.array(stockTakeSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
});
/* -------------------------------------------------------------------------- */
/* Product CSV import                                                         */
/* -------------------------------------------------------------------------- */
/** One row the import could not write, with the reason a human needs. */
export const productImportErrorSchema = z.object({
    /** 1-based row number in the uploaded file, header excluded. */
    row: z.number(),
    name: z.string().optional(),
    message: z.string(),
});
/**
 * `POST /products/import-csv` response.
 *
 * `errors` is deliberately part of the success payload rather than a failure:
 * an import that wrote 180 of 200 rows succeeded, and the operator needs the
 * 20 that did not, by row number. Rows are never silently dropped.
 */
export const importProductsCsvResponseSchema = z.object({
    created: z.number(),
    updated: z.number(),
    skipped: z.number(),
    categoriesCreated: z.number(),
    brandsCreated: z.number(),
    errors: z.array(productImportErrorSchema),
    /** Headers the mapper could not place; their values went to the description. */
    unmappedHeaders: z.array(z.string()),
});
//# sourceMappingURL=inventory.js.map